import { injectable, interfaces } from 'inversify';
import { action, IObservableArray, makeObservable, observable } from 'mobx';
import { TimePeriod } from '../../../design/selects/timePeriodSelector/S_TimePeriodSelector';
import { nameOf } from '../../helpers/object.helpers';
import { IPagingInfo, PaginationInfoDefault } from '../../models/paginationInfo.model';
import { SortingDirection } from '../../models/sortingDirection.model';
import { ITeamMember } from './models/TeamMember';

export interface ITeamStore {
  readonly orderBy: string;
  readonly orderDirection: SortingDirection;
  readonly pagination: IPagingInfo;
  readonly statisticsTimePeriod: TimePeriod;
  readonly teamMembers: IObservableArray<ITeamMember>;
  readonly isLoading: boolean;
  setPagination(pageNumber: number, pageSize: number): void;
  setSorting(orderBy: string, orderDirection: SortingDirection): void;
  setStatisticsTimePeriod(timePeriod: TimePeriod): void;
  setTeamMembers(teamMembers: ITeamMember[]): void;
  setIsLoading(isLoading: boolean): void;
}

export namespace ITeamStore {
  export const $: interfaces.ServiceIdentifier<ITeamStore> = Symbol('IMPORT_ANNOTATIONS_STORE');
}

const defaultSortingBy: string = nameOf<ITeamMember>('email');
const defaultSortingDirection: SortingDirection = SortingDirection.ASC;

@injectable()
export class TeamStore implements ITeamStore {
  teamMembers: IObservableArray<ITeamMember> = observable.array([]);

  orderBy: string = defaultSortingBy;

  orderDirection = defaultSortingDirection;

  statisticsTimePeriod = TimePeriod.Month;

  pagination = PaginationInfoDefault;

  isLoading: boolean = false;

  constructor() {
    makeObservable(this, {
      teamMembers: observable,
      orderBy: observable,
      orderDirection: observable,
      statisticsTimePeriod: observable,
      pagination: observable,
      isLoading: observable,
      setTeamMembers: action,
      setSorting: action,
      setStatisticsTimePeriod: action,
      setPagination: action,
      setIsLoading: action,
    });
  }

  setTeamMembers(teamMembers: ITeamMember[]): void {
    this.teamMembers.replace(teamMembers);
    this.pagination.totalCount = this.teamMembers.length;
    this.pagination.pageNumber = 1;
  }

  setSorting(orderBy: string, orderDirection: SortingDirection): void {
    this.orderBy = orderBy;
    this.orderDirection = orderDirection;
  }

  setStatisticsTimePeriod(timePeriod: TimePeriod): void {
    this.statisticsTimePeriod = timePeriod;
  }

  setPagination(pageNumber: number, pageSize: number): void {
    this.pagination.pageNumber = pageNumber;
    this.pagination.pageSize = pageSize;
  }

  setIsLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
  }
}
